import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';

import { GetglobalsettingsService } from './getglobalsettings.service';
import { AgentBankCredential } from '../shared/agent-bank-credentials';

/**
 * REST client for banking integration chunk P2, `/api/agent/bank-credentials`
 * on the webapi backend — CRUD for `AgentBankCredentials`. A separate service
 * file per this repo's own "one service per API area" convention
 * (`docs/PROMPT_agent-admin-panels.md`'s "Shared service" section) — this is
 * a different API area from `/api/agent/admin/*`, the same reasoning that
 * already gave `AgentSpamQueueApiService` its own file.
 *
 * Every route is `realm:portal-admin` server-side; this service does not
 * itself gate access — the host component/page does. Errors are NOT caught/
 * alerted here — callers subscribe with their own `error` handler and
 * surface it via `MessageService` toast, matching `agent-admin-api.service.ts`.
 */
@Injectable({
  providedIn: 'root'
})
export class AgentBankCredentialsApiService {

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /** Builds an absolute API URL by prepending the runtime API server address. */
  private _getApiUrl(url: string = ''): string {
    return this.globalsettingsservice.getApiServerUrl() + url;
  }

  /** Fresh standard headers per call so Setup-screen overrides take effect
   *  without a page reload (same reasoning as `agent-admin-api.service.ts`). */
  private _options() {
    return { headers: this.globalsettingsservice.getStandardApiHeaders() };
  }

  /** `GET /api/agent/bank-credentials` — full list, unfiltered. The admin
   *  console shows every entity's rows at once; per-entity filtering
   *  (`?cliid=`, which the backend supports) is a P-later frontend nicety,
   *  not needed here. */
  getBankCredentials(): Observable<AgentBankCredential[]> {
    return this.http.get<AgentBankCredential[]>(this._getApiUrl('/api/agent/bank-credentials'), this._options())
      .pipe(retry(1));
  }

  /** `POST /api/agent/bank-credentials` — insert a new row. Returns `{ id }`. */
  createBankCredential(row: Partial<AgentBankCredential>): Observable<{ id: number }> {
    const { id, created_at, uid, cloneIndex, ...body } = row as AgentBankCredential;
    return this.http.post<{ id: number }>(this._getApiUrl('/api/agent/bank-credentials'), body, this._options())
      .pipe(retry(1));
  }

  /** `PUT /api/agent/bank-credentials/:id` — update an existing row.
   *  `created_at` is a server-derived read-only field and is stripped before
   *  sending even if present on the input object. */
  updateBankCredential(row: AgentBankCredential): Observable<{ id: number }> {
    const { id, created_at, uid, cloneIndex, ...body } = row;
    return this.http.put<{ id: number }>(this._getApiUrl('/api/agent/bank-credentials/' + row.id), body, this._options())
      .pipe(retry(1));
  }

  /** `DELETE /api/agent/bank-credentials/:id` — a real delete, unlike
   *  `AgentTeamMember`'s `active` soft-deactivate flag; this table has no
   *  such flag (see the shared model's own comment). */
  deleteBankCredential(id: number): Observable<{ id: number }> {
    return this.http.delete<{ id: number }>(this._getApiUrl('/api/agent/bank-credentials/' + id), this._options());
  }
}
